import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { t } from '@lingui/macro'
import { format } from 'date-fns/esm'
import { useState } from 'react'
import styled, { useTheme } from 'styled-components'

import { TextArea } from '@/@bothrs-ui/web/components/forms/text-area'
import { FlexColumn, FlexRow } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { Line } from '@/@bothrs-ui/web/components/primitives/line/Line'
import { P, Span } from '@/@bothrs-ui/web/components/typography/Body'
import { mixpanel } from '@/mixpanel'
import { capitalize } from '@/modules/shared/helpers/locale'
import { openUrl } from '@/utils/external-resources'
import { useTranslated } from '@/utils/translation'
import { DoctorInfo } from '../../components/doctor-info/DoctorInfo'
import { BookingFlowActions, BookingFlowCenteredContent, BookingFlowLayout } from './BookingFlowLayout'

import type { FlowState, NavigationRequest } from '../types/booking.type'

const StLink = styled.button`
  color: ${({ theme }) => theme.Colors.Neutrals.Darker};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin: 0;
`

function CancellationDisclaimer() {
  const theme = useTheme()
  const title = useTranslated('cancellation-policy.title')
  const underlined = useTranslated('cancellation-policy.title.underlined')
  const url = useTranslated('cancellation-policy.url')

  const handleCancellationPolicyClick = () => {
    mixpanel.track('cancellation_policy_click')
    openUrl(url)
  }

  // If no URL, just display the title without any links
  if (!url) {
    return (
      <FlexRow gap={theme.UI.SpacingPx.Space2} style={{ marginTop: theme.UI.SpacingPx.Space4 }}>
        <FontAwesomeIcon icon={['fal', 'info-circle']} size='lg' />
        <P type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
          {title}
        </P>
      </FlexRow>
    )
  }

  // If URL exists, determine what to link
  const hasUnderlined = underlined && title.includes(underlined)
  const underlinedIndex = title.indexOf(underlined)
  const beforeUnderlined = hasUnderlined ? title.slice(0, underlinedIndex) : ''
  const underlinedPart = hasUnderlined ? underlined : title
  const afterUnderlined = hasUnderlined ? title.slice(underlinedIndex + underlined.length) : ''

  return (
    <FlexRow gap={theme.UI.SpacingPx.Space2} style={{ marginTop: theme.UI.SpacingPx.Space4 }}>
      <FontAwesomeIcon icon={['fal', 'info-circle']} size='lg' />
      <P type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
        {beforeUnderlined}
        <StLink onClick={handleCancellationPolicyClick}>{underlinedPart}</StLink>
        {afterUnderlined}
      </P>
    </FlexRow>
  )
}

type ConfirmationContentProps = {
  flowState: FlowState
  onAction: (action: NavigationRequest['action'], data?: Record<string, unknown>) => void
  isLoading?: boolean
}

export const ConfirmationContent = ({ flowState, onAction, isLoading }: ConfirmationContentProps) => {
  const theme = useTheme()

  const [remark, setRemark] = useState('')
  const slot = flowState.selectedSlot

  if (!slot) {
    return null
  }

  const onConfirm = () => {
    onAction('confirm', { remark })
    mixpanel.track('booking_flow_step_completed', {
      withTriage: !!flowState.triagePath?.length,
      withRemark: !!remark,
    })
  }

  return (
    <BookingFlowLayout>
      <BookingFlowCenteredContent gap={theme.UI.SpacingPx.Space6}>
        <Span type='BodyLargeRegular'>
          {t({
            id: 'appointments.book.confirmation.title',
            comment: 'Confirmation title',
            message: 'Bevestig onderstaande afspraak om ze in te plannen.',
          })}
        </Span>

        {slot.doctor ? (
          <DoctorInfo doctor={slot.doctor} size={'large'} description={flowState.specialisation?.title?.nl} />
        ) : null}

        <Line />

        <FlexRow alignItems='center' gap={theme.UI.SpacingPx.Space4}>
          <FlexRow alignItems='center' gap={theme.UI.SpacingPx.Space2}>
            <FontAwesomeIcon icon={['fal', 'calendar']} />
            <Span
              type='BodyMediumRegular'
              style={{
                fontWeight: 500,
              }}
            >
              {capitalize(format(new Date(slot.start), 'EEE dd/MM/yyyy'))}
            </Span>
          </FlexRow>

          <FlexRow alignItems='center' gap={theme.UI.SpacingPx.Space2}>
            <FontAwesomeIcon icon={['fal', 'clock']} color={theme.Colors.Neutrals.Darker} />
            <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
              {format(new Date(slot.start), 'HH:mm')}
            </Span>
          </FlexRow>
        </FlexRow>
        <FlexColumn>
          <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
            {slot.location.name}
          </Span>
          <Span type='BodyMediumRegular' color={theme.Colors.Neutrals.Darker}>
            {slot.location.address}
          </Span>
        </FlexColumn>

        <Line />

        <TextArea
          name={'remark'}
          label={t({
            id: 'appointments.book.confirm.remark',
            comment: 'Remark field label',
            message: 'Extra opmerking (Optioneel)',
          })}
          placeholder={t({
            id: 'appointments.book.confirm.remark.placeholder',
            message: 'Noteer hier je opmerking(en).',
          })}
          maxLength={255}
          value={remark}
          onChange={(value) => setRemark(value)}
        />

        <CancellationDisclaimer />
      </BookingFlowCenteredContent>

      <BookingFlowActions>
        <Button variant='Primary' onClick={onConfirm} disabled={isLoading} isLoading={isLoading}>
          {t({
            id: 'appointments.book.confirm.confirm',
            message: 'Afspraak bevestigen',
          })}
        </Button>
      </BookingFlowActions>
    </BookingFlowLayout>
  )
}
